// =============================================================================
// ChatList/useChannelOptions.ts — caixas do filtro de Conversas (F58-S12).
//
// Antes, o filtro lia `useChannels()` (`GET /api/channels`), que exige
// `channel.connect` — só o admin tem em produção. Todo outro papel levava 403,
// o hook devolvia `[]` e o seletor mostrava só "Todas" (achado CONV-01).
//
// `GET /api/conversations/channel-options` pede o MESMO piso da lista
// (`livechat:conversation:read`) e devolve só as caixas em que o usuário vê ao
// menos uma conversa, mais o WhatsApp pessoal dele. `useChannels` continua
// intocado: ele serve a tela de gestão de canais, que é outro contrato.
//
// LGPD: a resposta é mínima (id, nome, origem) — sem telefone nem sessão.
// =============================================================================

import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { api } from '../../../../lib/api';

const ChannelOptionSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  provider: z.enum(['meta_whatsapp', 'meta_instagram', 'waha']),
});

const ChannelOptionsResponseSchema = z.object({
  data: z.array(ChannelOptionSchema),
});

export type ChannelOption = z.infer<typeof ChannelOptionSchema>;

/**
 * Mesmo padrão de `conversationLeadCitiesKey`: sob o prefixo `conversations`,
 * então as mutações que invalidam `conversationKeys.all` (iniciar conversa,
 * vincular lead, retry do erro da lista) também o atualizam — é justamente
 * quando uma caixa pode ganhar a primeira conversa visível. As invalidações
 * por mensagem do socket miram chaves específicas e não o tocam.
 */
export const channelOptionsQueryKey = ['conversations', 'channelOptions'] as const;

/** queryFn do hook — exportada para teste sem DOM (padrão do projeto). */
export async function fetchChannelOptions(): Promise<ChannelOption[]> {
  const raw = await api.get<unknown>('/api/conversations/channel-options');
  return ChannelOptionsResponseSchema.parse(raw).data;
}

/** Opções do dropdown de caixa — só o que `ChatListFilters` consome. */
export function toFilterOptions(
  options: readonly ChannelOption[],
): { readonly id: string; readonly name: string }[] {
  return options.map((option) => ({ id: option.id, name: option.name }));
}

export function useChannelOptions(): {
  options: readonly ChannelOption[];
  isLoading: boolean;
} {
  const { data, isLoading } = useQuery({
    queryKey: channelOptionsQueryKey,
    queryFn: fetchChannelOptions,
    // O conjunto de caixas muda devagar (caixa nova, primeira conversa numa
    // caixa) — mesmo ritmo do filtro de cidades.
    staleTime: 5 * 60 * 1000,
  });

  return { options: data ?? [], isLoading };
}
